import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Download, Upload, Edit2, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";
import {
  parseDecimal,
  parseIntPositive,
  parseNumericString,
  parseUf,
  validateRows,
  formatValidationErrors,
  type ValidationError,
} from "../lib/fiscalImportUtils";

export default function MVA_ST() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUpdateBulkDialogOpen, setIsUpdateBulkDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isNatureModalOpen, setIsNatureModalOpen] = useState(false);
  const [natureSearchTerm, setNatureSearchTerm] = useState("");
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isBulkNatureModalOpen, setIsBulkNatureModalOpen] = useState(false);
  const [bulkNatureSearchTerm, setBulkNatureSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    ncm: "",
    ufOrigem: "",
    ufDestino: "",
    mvaAjustada4: "",
    mvaAjustada7: "",
    mvaAjustada12: "",
    mvaOriginal: "",
    naturezaOperacaoId: undefined as number | undefined,
    naturezaOperacaoNome: "",
    gatilho: "" as string,
    emitenteId: undefined as number | undefined,
    emitenteNome: "",
  });

  const [bulkUpdateData, setBulkUpdateData] = useState({
    ufOrigem: "",
    ufDestino: "",
    mvaAjustada4: "",
    mvaAjustada7: "",
    mvaAjustada12: "",
    mvaOriginal: "",
    naturezaOperacaoId: undefined as number | undefined,
    gatilho: undefined as string | undefined,
  });

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: mvaStList = [], refetch } = trpc.mvaSt.getAll.useQuery();
  const { data: operationNatures = [] } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  // Mutations
  const createMutation = trpc.mvaSt.create.useMutation({
    onSuccess: () => {
      toast.success("MVA-ST criado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar MVA-ST");
    },
  });

  const bulkCreateMutation = trpc.mvaSt.createBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.ids.length} registros importados com sucesso!`);
      setIsBulkDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao importar registros");
    },
  });

  const updateMutation = trpc.mvaSt.update.useMutation({
    onSuccess: () => {
      toast.success("MVA-ST atualizado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar MVA-ST");
    },
  });

  const bulkUpdateMutation = trpc.mvaSt.updateBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registros atualizados com sucesso!`);
      setIsUpdateBulkDialogOpen(false);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar registros");
    },
  });
  const deleteMutation = trpc.mvaSt.deleteBatch.useMutation({ onSuccess: (data) => {
      toast.success(`${data.count} registro(s) excluído(s) com sucesso!`);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir registros");
    },
  });

  const resetForm = () => {
    setFormData({
      ncm: "",
      ufOrigem: "",
      ufDestino: "",
      mvaAjustada4: "",
      mvaAjustada7: "",
      mvaAjustada12: "",
      mvaOriginal: "",
      naturezaOperacaoId: undefined,
      naturezaOperacaoNome: "",
      gatilho: "",
      emitenteId: undefined,
      emitenteNome: "",
    });
  };

  const handleOpenDialog = (mvaSt?: any) => {
    if (mvaSt) {
      setEditingId(mvaSt.id);
      setFormData({
        ncm: mvaSt.ncm,
        ufOrigem: mvaSt.ufOrigem,
        ufDestino: mvaSt.ufDestino,
        mvaAjustada4: mvaSt.mvaAjustada4 !== null && mvaSt.mvaAjustada4 !== undefined ? String(mvaSt.mvaAjustada4) : "",
        mvaAjustada7: mvaSt.mvaAjustada7 !== null && mvaSt.mvaAjustada7 !== undefined ? String(mvaSt.mvaAjustada7) : "",
        mvaAjustada12: mvaSt.mvaAjustada12 !== null && mvaSt.mvaAjustada12 !== undefined ? String(mvaSt.mvaAjustada12) : "",
        mvaOriginal: mvaSt.mvaOriginal !== null && mvaSt.mvaOriginal !== undefined ? String(mvaSt.mvaOriginal) : "",
        naturezaOperacaoId: mvaSt.naturezaOperacaoId || undefined,
        naturezaOperacaoNome: mvaSt.naturezaOperacaoNome || "",
        gatilho: mvaSt.gatilho || "",
        emitenteId: mvaSt.emitenteId || undefined,
        emitenteNome: mvaSt.emitenteNome || "",
      });
    } else {
      setEditingId(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.ncm || !formData.ufOrigem || !formData.ufDestino || !formData.naturezaOperacaoId || !formData.gatilho) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (formData.ncm.length !== 8) {
      toast.error("NCM deve ter 8 dígitos");
      return;
    }

    const payload = {
      ncm: formData.ncm,
      ufOrigem: formData.ufOrigem,
      ufDestino: formData.ufDestino,
      mvaAjustada4: formData.mvaAjustada4 !== "" ? parseFloat(formData.mvaAjustada4) : undefined,
      mvaAjustada7: formData.mvaAjustada7 !== "" ? parseFloat(formData.mvaAjustada7) : undefined,
      mvaAjustada12: formData.mvaAjustada12 !== "" ? parseFloat(formData.mvaAjustada12) : undefined,
      mvaOriginal: formData.mvaOriginal !== "" ? parseFloat(formData.mvaOriginal) : undefined,
      naturezaOperacaoId: formData.naturezaOperacaoId,
      gatilho: formData.gatilho,
      emitenteId: formData.emitenteId,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredMvaSt.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMvaSt.map((mvaSt) => mvaSt.id));
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    if (confirm(`Deseja realmente excluir ${selectedIds.length} registro(s)?`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  };

  const handleExport = () => {
    const data = filteredMvaSt.map((mvaSt) => ({
      "NCM": mvaSt.ncm,
      "UF Origem": mvaSt.ufOrigem,
      "UF Destino": mvaSt.ufDestino,
      "MVA Ajustada 4%": mvaSt.mvaAjustada4 || "",
      "MVA Ajustada 7%": mvaSt.mvaAjustada7 || "",
      "MVA Ajustada 12%": mvaSt.mvaAjustada12 || "",
      "MVA Original": mvaSt.mvaOriginal || "",
      "Natureza ID": mvaSt.naturezaOperacaoId || "",
      "Natureza de Operação": mvaSt.natureza_operacao_nome || "",
      "Gatilho": mvaSt.gatilho || "",
      "Emitente ID": mvaSt.emitenteId || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MVA-ST");
    XLSX.writeFile(wb, "mva_st.xlsx");
    toast.success("Arquivo exportado com sucesso!");
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);

        const errors = validateRows(json, (row, i) => {
          const errs: ValidationError[] = [];
          const linha = i + 2;
          if (parseNumericString(row["NCM"] ?? row["ncm"]) === null) errs.push({ linha, campo: "NCM", motivo: "obrigatório, deve conter apenas dígitos" });
          if (!parseUf(row["UF Origem"] ?? row["ufOrigem"])) errs.push({ linha, campo: "UF Origem", motivo: "obrigatório, deve ter 2 letras" });
          if (!parseUf(row["UF Destino"] ?? row["ufDestino"])) errs.push({ linha, campo: "UF Destino", motivo: "obrigatório, deve ter 2 letras" });
          return errs;
        });

        if (errors.length > 0) {
          toast.error(formatValidationErrors(errors), { duration: 8000 });
          return;
        }

        const items = json.map((row) => ({
          ncm: parseNumericString(row["NCM"] ?? row["ncm"]) as string,
          ufOrigem: parseUf(row["UF Origem"] ?? row["ufOrigem"]) as string,
          ufDestino: parseUf(row["UF Destino"] ?? row["ufDestino"]) as string,
          mvaAjustada4: parseDecimal(row["MVA Ajustada 4%"] ?? row["mvaAjustada4"]) ?? undefined,
          mvaAjustada7: parseDecimal(row["MVA Ajustada 7%"] ?? row["mvaAjustada7"]) ?? undefined,
          mvaAjustada12: parseDecimal(row["MVA Ajustada 12%"] ?? row["mvaAjustada12"]) ?? undefined,
          mvaOriginal: parseDecimal(row["MVA Original"] ?? row["mvaOriginal"]) ?? undefined,
          naturezaOperacaoId: parseIntPositive(row["Natureza ID"]) ?? undefined,
          gatilho: row["Gatilho"] ?? row["gatilho"] ?? undefined,
          emitenteId: parseIntPositive(row["Emitente ID"]) ?? undefined,
        }));

        bulkCreateMutation.mutate(items);
      } catch (error) {
        toast.error("Erro ao processar arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleUpdateBulk = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    const payload: any = {};
    if (bulkUpdateData.ufOrigem) payload.ufOrigem = bulkUpdateData.ufOrigem;
    if (bulkUpdateData.ufDestino) payload.ufDestino = bulkUpdateData.ufDestino;
    if (bulkUpdateData.mvaAjustada4) payload.mvaAjustada4 = parseFloat(bulkUpdateData.mvaAjustada4);
    if (bulkUpdateData.mvaAjustada7) payload.mvaAjustada7 = parseFloat(bulkUpdateData.mvaAjustada7);
    if (bulkUpdateData.mvaAjustada12) payload.mvaAjustada12 = parseFloat(bulkUpdateData.mvaAjustada12);
    if (bulkUpdateData.mvaOriginal) payload.mvaOriginal = parseFloat(bulkUpdateData.mvaOriginal);
    if (bulkUpdateData.naturezaOperacaoId) payload.naturezaOperacaoId = bulkUpdateData.naturezaOperacaoId;
    if (bulkUpdateData.gatilho) payload.gatilho = bulkUpdateData.gatilho;

    if (Object.keys(payload).length === 0) {
      toast.error("Preencha pelo menos um campo para atualizar");
      return;
    }

    bulkUpdateMutation.mutate({ ids: selectedIds, data: payload });
  };

  const gatilhoLabels: Record<string, string> = {
    consumidor_final: "Consumidor Final",
    sem_st: "Venda sem ST",
    com_st: "Venda com ST",
    alc: "ALC (Área de Livre Comércio)",
    zona_franca: "Zona Franca de Manaus",
    amazonia_ocidental: "AO (Amazônia Ocidental)",
    nenhum: "Nenhum",
  };

  const filteredMvaSt = mvaStList.filter((mvaSt) => {
    const search = searchTerm.toLowerCase();
    return (
      mvaSt.ncm.toLowerCase().includes(search) ||
      mvaSt.ufOrigem.toLowerCase().includes(search) ||
      mvaSt.ufDestino.toLowerCase().includes(search) ||
      (mvaSt.mvaAjustada4 && mvaSt.mvaAjustada4.toString().includes(search)) ||
      (mvaSt.mvaAjustada7 && mvaSt.mvaAjustada7.toString().includes(search)) ||
      (mvaSt.mvaAjustada12 && mvaSt.mvaAjustada12.toString().includes(search)) ||
      (mvaSt.mvaOriginal && mvaSt.mvaOriginal.toString().includes(search)) ||
      (mvaSt.natureza_operacao_nome && mvaSt.natureza_operacao_nome.toLowerCase().includes(search))
    );
  });

  if (authLoading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>MVA-ST</CardTitle>
              <CardDescription>Gerencie as alíquotas de MVA-ST por NCM e UF</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
              <Button variant="outline" size="sm" asChild>
                <label>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
                </label>
              </Button>
              {selectedIds.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsUpdateBulkDialogOpen(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Alterar Selecionados
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Selecionados
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Novo MVA-ST
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Buscar por NCM, UF ou MVA..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredMvaSt.length && filteredMvaSt.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">NCM</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">UF Origem</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">UF Destino</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">MVA Ajust. 4%</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">MVA Ajust. 7%</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">MVA Ajust. 12%</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">MVA Original</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Natureza</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Emitente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Gatilho</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredMvaSt.map((mvaSt) => (
                  <tr key={mvaSt.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(mvaSt.id)}
                        onChange={() => handleSelectOne(mvaSt.id)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{mvaSt.ncm}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.ufOrigem}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.ufDestino}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.mvaAjustada4 || "-"}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.mvaAjustada7 || "-"}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.mvaAjustada12 || "-"}</td>
                    <td className="px-4 py-3 text-sm">{mvaSt.mvaOriginal || "-"}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {mvaSt.natureza_operacao_nome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {mvaSt.emitenteNome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {mvaSt.gatilho ? (gatilhoLabels[mvaSt.gatilho] || mvaSt.gatilho) : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="link" size="sm" onClick={() => handleOpenDialog(mvaSt)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Novo"} MVA-ST</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>NCM (8 dígitos) *</Label>
              <Input
                type="text"
                maxLength={8}
                value={formData.ncm}
                onChange={(e) => setFormData({ ...formData, ncm: e.target.value.replace(/\D/g, '') })}
                placeholder="12345678"
              />
            </div>

            <div>
              <Label>UF Origem *</Label>
              <Input
                type="text"
                maxLength={2}
                value={formData.ufOrigem}
                onChange={(e) => setFormData({ ...formData, ufOrigem: e.target.value.toUpperCase() })}
                placeholder="SP"
              />
            </div>

            <div>
              <Label>UF Destino *</Label>
              <Input
                type="text"
                maxLength={2}
                value={formData.ufDestino}
                onChange={(e) => setFormData({ ...formData, ufDestino: e.target.value.toUpperCase() })}
                placeholder="RJ"
              />
            </div>

            <div>
              <Label>MVA Ajustada 4%</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.mvaAjustada4}
                onChange={(e) => setFormData({ ...formData, mvaAjustada4: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Ajustada 7%</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.mvaAjustada7}
                onChange={(e) => setFormData({ ...formData, mvaAjustada7: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Ajustada 12%</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.mvaAjustada12}
                onChange={(e) => setFormData({ ...formData, mvaAjustada12: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Original</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.mvaOriginal}
                onChange={(e) => setFormData({ ...formData, mvaOriginal: e.target.value })}
              />
            </div>

            <div>
              <Label>Natureza de Operação *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.naturezaOperacaoNome}
                  readOnly
                  placeholder="Selecione uma natureza de operação"
                />
                <Button
                  type="button"
                  onClick={() => setIsNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {formData.naturezaOperacaoId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, naturezaOperacaoId: undefined, naturezaOperacaoNome: "" })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho *</Label>
              <Select
                value={formData.gatilho || ""}
                onValueChange={(value) => setFormData({ ...formData, gatilho: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!user?.emitenteId && (
              <div>
                <Label>Emitente *</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.emitenteNome}
                    readOnly
                    placeholder="Selecione um emitente"
                  />
                  <Button
                    type="button"
                    onClick={() => setIsEmitenteModalOpen(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Alteração em Massa */}
      <Dialog open={isUpdateBulkDialogOpen} onOpenChange={setIsUpdateBulkDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alterar {selectedIds.length} Registro(s) em Massa</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>UF Origem</Label>
              <Input
                type="text"
                maxLength={2}
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.ufOrigem}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, ufOrigem: e.target.value.toUpperCase() })}
              />
            </div>

            <div>
              <Label>UF Destino</Label>
              <Input
                type="text"
                maxLength={2}
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.ufDestino}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, ufDestino: e.target.value.toUpperCase() })}
              />
            </div>

            <div>
              <Label>MVA Ajustada 4%</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.mvaAjustada4}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, mvaAjustada4: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Ajustada 7%</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.mvaAjustada7}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, mvaAjustada7: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Ajustada 12%</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.mvaAjustada12}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, mvaAjustada12: e.target.value })}
              />
            </div>

            <div>
              <Label>MVA Original</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.mvaOriginal}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, mvaOriginal: e.target.value })}
              />
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={bulkUpdateData.naturezaOperacaoId
                    ? (operationNatures.find((n: any) => n.id === bulkUpdateData.naturezaOperacaoId) as any)?.nome || ""
                    : ""}
                  readOnly
                  placeholder="Não alterar"
                />
                <Button
                  type="button"
                  onClick={() => setIsBulkNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {bulkUpdateData.naturezaOperacaoId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: undefined })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho</Label>
              <Select
                value={bulkUpdateData.gatilho || "unchanged"}
                onValueChange={(value) => setBulkUpdateData({ ...bulkUpdateData, gatilho: value === "unchanged" ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unchanged">Não alterar</SelectItem>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsUpdateBulkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateBulk}>
              Atualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal de Seleção de Natureza de Operação — Formulário */}
      <Dialog open={isNatureModalOpen} onOpenChange={setIsNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={natureSearchTerm}
            onChange={(e) => setNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((n: any) =>
                    natureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(natureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(natureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setFormData({ ...formData, naturezaOperacaoId: n.id, naturezaOperacaoNome: `${n.nome} — ${n.tipo}` });
                            setNatureSearchTerm("");
                            setIsNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Natureza de Operação — Bulk Update */}
      <Dialog open={isBulkNatureModalOpen} onOpenChange={setIsBulkNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={bulkNatureSearchTerm}
            onChange={(e) => setBulkNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((n: any) =>
                    bulkNatureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(bulkNatureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: n.id });
                            setBulkNatureSearchTerm("");
                            setIsBulkNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Razão Social</th>
                  <th className="border-b p-3 text-left">CNPJ</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(emitentes as any[]).map((em: any) => (
                  <tr key={em.id} className="hover:bg-gray-50">
                    <td className="border-b p-3">{em.id}</td>
                    <td className="border-b p-3">{em.razaoSocial}</td>
                    <td className="border-b p-3">{em.cnpj}</td>
                    <td className="border-b p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, emitenteId: em.id, emitenteNome: em.razaoSocial });
                          setIsEmitenteModalOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        Selecionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
